/**
 * VilaxMobileNav — the OWNED mobile navigation (DR-2026-203 frontend-A, #1 paydown).
 *
 * Replaces VLT's MobileNavigation + the (VLT-8-deleted) MobileToolsFooter + the
 * ~180 lines of SCSS that neutralized pastanaga's hamburger (BUG-2026-115/097/103,
 * DR-2026-109). Compose-first: own classes (`.vmn-*`), no upstream hamburger to
 * fight, no VLT shadow. This is the single biggest VLT-internal coupling retired
 * while otherwise staying on Approach A.
 *
 * Closed: a 3-bar hamburger (28x20, currentColor → inherits the header's
 * white-on-overlay / green-on-solid). Open: a drawer slides in from the right
 * with search + primary navigation + language selector; the bars cross into an X.
 * Hidden >991px via vilax-mobile.css (desktop shows the inline header nav instead).
 *
 * Drawer content is faithful to the live VLT-7 vilax drawer (search + nav + lang) —
 * the spike-B repro carried nav+lang only; SearchWidget is restored here so the
 * cutover does not regress mobile search.
 */
import { useState } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { defineMessages, useIntl } from 'react-intl';
import { flattenToAppURL } from '@plone/volto/helpers';
import LanguageSelector from '@plone/volto/components/theme/LanguageSelector/LanguageSelector';
import SearchWidget from '@plone/volto/components/theme/SearchWidget/SearchWidget';

const messages = defineMessages({
  home: {
    id: 'Home',
    defaultMessage: 'Home',
  },
});

const VilaxMobileNav = ({ pathname }) => {
  const [open, setOpen] = useState(false);
  // BUG-2026-215: the drawer previously embedded core <Navigation/>, whose
  // items live inside Semantic's "computer large screen widescreen only"
  // wrapper — display:none at every width the drawer exists (≤991px), so the
  // drawer nav was invisible since the DR-2026-203 cutover. Owned list from
  // the same navigation state instead (compose-first, no Semantic classes).
  const items = useSelector(
    (state) => state.navigation?.items || [],
    shallowEqual,
  );
  // DR-2026-251: with the drawer open the logo is covered, so the drawer
  // needs its own path home. The language root comes from the navroot state
  // (populated per-language since BUG-2026-214); the label is Volto's stock
  // 'Home' message — localized in every shipped locale.
  const intl = useIntl();
  const navroot = useSelector(
    (state) => state.navroot?.data?.navroot,
    shallowEqual,
  );
  const homeUrl = navroot?.['@id'] ? flattenToAppURL(navroot['@id']) : null;

  return (
    <div className={`vmn${open ? ' vmn--open' : ''}`}>
      <button
        type="button"
        className="vmn-toggler"
        aria-label={open ? 'Close menu' : 'Open menu'}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="vmn-hamburger" aria-hidden="true">
          <span className="vmn-bar" />
          <span className="vmn-bar" />
          <span className="vmn-bar" />
        </span>
      </button>

      <div className="vmn-drawer" role="dialog" aria-modal="true" aria-hidden={!open}>
        <div className="vmn-drawer__search">
          <SearchWidget />
        </div>
        <nav className="vmn-drawer__nav" aria-label="Mobile navigation">
          <ul>
            {homeUrl && (
              <li key={homeUrl}>
                <a
                  className={`item${pathname === homeUrl ? ' active' : ''}`}
                  href={homeUrl}
                >
                  {intl.formatMessage(messages.home)}
                </a>
              </li>
            )}
            {items.map((item) => {
              const url = flattenToAppURL(item.url);
              return (
                <li key={url}>
                  <a
                    className={`item${pathname === url ? ' active' : ''}`}
                    href={url}
                  >
                    {item.title}
                  </a>
                </li>
              );
            })}
          </ul>
        </nav>
        <div className="vmn-drawer__lang">
          <LanguageSelector />
        </div>
      </div>

      {open && (
        <button
          type="button"
          className="vmn-scrim"
          aria-label="Close menu"
          onClick={() => setOpen(false)}
        />
      )}
    </div>
  );
};

export default VilaxMobileNav;
